import { setOpen, setCreateAccountSelectorOpen, setTitle, setActiveRole, clearActiveRole } from './AccountSwitcher.reducer';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/ReduxHook';
import { useAuthLogic } from '../../../../Auth/logic/useAuth.logic';
import { useState } from 'react';
import { useLazyListRolesQuery, useListRolesQuery } from './AccountSwitcher.api';
import { useNotification } from '../../../../../../hooks/useNotification';
import { USER_ACTIVE_ROLE_ID_STORAGE_KEY } from './constant';
import { setHasSelected } from '../../CreateAccount/logic/CreateAccount.reducer';
import { resumePendingCallback } from '../../../../Auth/logic/accountSetupStorage';
import { AUTH_LOCAL_STORAGE_KEY } from '../../../../Auth/logic/constants';

export const useAccountSwitcher = () => {
  const state = useAppSelector((state) => state.accountSwitcher);
  const rootState = useAppSelector((state) => state);
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const { doSwitchRole, isLoading: isLoadingAuth } = useAuthLogic();
  const { isLoading, data, error, refetch } = useListRolesQuery();
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);

  //////////////////////////////////////////
  const getActiveRole = () => localStorage.getItem(USER_ACTIVE_ROLE_ID_STORAGE_KEY); // This should probably be in auth
  //////////////////////////////////////////
  const doGoBack = () => {
    if (state.isCreateAccountSelectorOpen) {
      if (rootState?.createAccount?.hasSelected) {
        dispatch(setTitle({ title: 'Create account' }));
        dispatch(setHasSelected({ hasSelected: false }));
      } else {
        dispatch(setCreateAccountSelectorOpen({ isOpen: false }));
        dispatch(setTitle({ title: 'Switch to Account' }));
      }
    }
  };
  const doReset = () => {
    dispatch(setHasSelected({ hasSelected: false }));
    dispatch(setCreateAccountSelectorOpen({ isOpen: false }));
    dispatch(setTitle({ title: 'Switch to Account' }));
  };
  const doClose = () => {
    dispatch(setOpen({ isOpen: false }));
    dispatch(setTitle({ title: 'Switch to Account' }));
    dispatch(setCreateAccountSelectorOpen({ isOpen: false }));
  };
  const doSetModalTitle = (title: string) => dispatch(setTitle({ title }));
  const doToggleCreateAccount = (state: boolean) => dispatch(setCreateAccountSelectorOpen({ isOpen: state }));
  const doSetActiveRoleId = (roleId: string) => dispatch(setActiveRole({ roleId }));
  const doClearActiveRoleId = () => dispatch(clearActiveRole());
  const doToggleAccountSwitcher = (state: boolean) => dispatch(setOpen({ isOpen: state }));
  const doOpenCreateAccount = () => {
    dispatch(setOpen({ isOpen: true }));
    dispatch(setCreateAccountSelectorOpen({ isOpen: true }));
    dispatch(setTitle({ title: 'Create account' }));
  };
  const doSwitchAccount = (roleId: string) => {
    if (!roleId || roleId === state.activeRoleId) {
      return;
    }
    const date = +new Date();
    const username = rootState.auth.user?.username;
    const sessionToken = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    // A result for a previous session must not change the current session.
    const sessionChanged = () => localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) !== sessionToken;
    setPendingRoleId(roleId);
    doSwitchRole({ roleId, [date]: date })
      .unwrap()
      .then(() => {
        if (sessionChanged()) {
          return;
        }
        // Save the role only after the switch succeeds.
        dispatch(setActiveRole({ roleId }));
        if (username && resumePendingCallback(username, sessionToken)) {
          return;
        }
        notification.success({
          message: 'Account is Changed!',
          description: '',
        });
        setTimeout(() => {
          dispatch(setOpen({ isOpen: false }));
        }, 500);
      })
      .catch((error: { status?: unknown }) => {
        if (sessionChanged()) {
          return;
        }
        // The last confirmed role stays active and stored.
        notification.error({
          message:
            error?.status === 403 || error?.status === 404
              ? "You can't switch to this account."
              : "We couldn't switch accounts. Try again.",
          description: '',
        });
      })
      .finally(() => setPendingRoleId(null));
  };

  const onClickCreateAccount = () => {
    doToggleCreateAccount(true);
    doSetModalTitle('Create account');
  };

  return {
    error,
    isLoading: isLoading,
    isLoadingAuth: isLoadingAuth,
    pendingRoleId,
    listOfAccounts: data || [],

    modalTitle: state.title,
    isModalOpen: state.isOpen,
    isCreateAccountOpen: state.isCreateAccountSelectorOpen,

    getActiveRole,
    onClickCreateAccount,

    doClose,
    doGoBack,
    doReset,
    doSetModalTitle,
    doSetActiveRoleId,
    doClearActiveRoleId,
    doOpenCreateAccount,
    doSwitchAccount,
    doToggleAccountSwitcher,
    doToggleCreateAccount,
    refetch,
  };
};
